<template>
  <div
    v-intersect="{
      handler: visibilityChanged,
      options: {
        threshold: [0, 0.5, 1.0],
      },
    }"
    class="list-entry"
  >
    <div class="list-entry-header d-flex pl-2" :class="{ 'list-entry-header--active': isActive }">
      <div
        class="title-container font-weight-bold d-flex align-center justify-space-between"
        :title="title"
      >
        <h4 class="text-h6 text-uppercase">
          {{ title }}
        </h4>
        <v-chip v-if="isSpacebreakLocked(spacebreakId)" size="small" color="primary" class="locked">
          {{ tkey('locked') }}
        </v-chip>
      </div>
    </div>
    <!-- 30ms delay to ensure draggable content is always clickable -->
    <draggable
      v-if="showProducts"
      :model-value="renderedProducts[0]"
      item-key="productKey"
      :disabled="isDragDisabled"
      :group="groupName"
      :sort="false"
      :scroll-sensitivity="100"
      :invert-swap="true"
      :force-fallback="true"
      ghost-class="ghost"
      draggable=".draggable"
      delay="30"
      style="position: relative"
      @change="onChange"
      @start="onDragStart"
      @end="onDragStop"
    >
      <template #item="{ element, index }">
        <div
          :id="`tile-product-${element.productKey}`"
          :key="element._id"
          ref="parent-entry-item"
          class="entry-item"
          :class="{
            draggable: !isLocked(element) && !isDragDisabled,
            'pod-background-increase': get(element.deltas, 'reference') > 0 && element.isEligible,
            'pod-background-decrease': get(element.deltas, 'reference') < 0 && element.isEligible,
          }"
        >
          <ListViewProductEntry
            :product="element"
            :spacebreak-id="spacebreakId"
            :group-name="groupName"
            :list-view-ref="listViewRef"
            :parent-entry-item-ref="parentEntryItemRef"
            :index="index"
            :open-popup-on-load-for-product-id="productIdForPopup"
          />
        </div>
      </template>
    </draggable>
    <!-- Placeholder for when the product tile is off screen -->
    <div
      v-for="element in renderedProducts[1]"
      :id="`tile-product-${element.productKey}`"
      :key="element._id"
      ref="parent-entry-item"
      class="entry-item skeleton"
    >
      <img
        :id="`product-tile-popup-${element.productKey}`"
        src="@/assets/spinner.gif"
        @click="delayPopup(element._id)"
      />
    </div>
  </div>
</template>

<script>
// Detailed comments on the visibility and draggable
// functionailty in: product-tile-drag-container.vue
import { useInternationalization } from '@/composables/use-internationalization';
import { usePermission } from '@/composables/use-permission';
import { useCanvasDraggableVisibility } from '@/composables/use-canvas-draggable-visibility';
import { useAssortmentCanvasStore } from '@/stores/assortment-canvas';
import destroy from '@/utils/destroy';
import assortmentProductLockTypes from '@enums/assortment-product-lock-types';
import spacebreakTypes from '@enums/spacebreak-types';
import { get, includes } from 'lodash';
import { mapActions, mapState } from 'pinia';
import { useDomUtility } from '@/composables/dom-utils';
import { useCanvasEditability } from '@/composables/use-canvas-editability';

export default {
  props: {
    products: {
      type: Array,
      default: () => [],
      required: true,
    },

    title: {
      type: String,
      required: true,
    },

    spacebreakId: {
      type: String,
      default: '',
    },

    spacebreakType: {
      type: String,
      default: null,
    },

    groupName: {
      type: String,
      required: true,
    },

    listViewRef: {
      type: Object,
      required: true,
    },

    containerId: {
      type: String,
      default: '',
    },
  },

  setup() {
    const { canEditAssortmentCanvas } = usePermission();
    const { tkey } = useInternationalization('assortmentCanvasPage.listViewPage');
    const { addActiveSearchBorderToProductElement } = useDomUtility();
    const {
      delayPopup,
      onDragStart,
      onDragStop,
      productIdForPopup,
      showProducts,
      visibilityChanged,
    } = useCanvasDraggableVisibility();

    const { canvasCanBeEdited } = useCanvasEditability();

    return {
      canvasCanBeEdited,
      canEditAssortmentCanvas,
      tkey,
      addActiveSearchBorderToProductElement,
      delayPopup,
      onDragStart,
      onDragStop,
      productIdForPopup,
      showProducts,
      visibilityChanged,
    };
  },

  data() {
    return {
      get,
      parentEntryItemRef: null,
    };
  },

  computed: {
    ...mapState(useAssortmentCanvasStore, [
      'activeSpacebreak',
      'selectedCanvas',
      'isSpacebreakLocked',
    ]),

    isOptimised() {
      return get(this.selectedCanvas, 'hasBeenOptimised', false);
    },

    isActive() {
      return this.activeSpacebreak === (this.spacebreakId || this.spacebreakType);
    },

    isDragDisabled() {
      return (
        this.isSpacebreakLocked(this.spacebreakId) ||
        includes(spacebreakTypes.undraggableSpacebreaks, this.containerId) ||
        !this.canvasCanBeEdited
      );
    },

    // Calculate the list of products that should be rendered (first position rendered)
    renderedProducts() {
      if (this.showProducts) {
        // Should be rendered
        return [this.products, []];
      }
      return [[], this.products];
    },
  },

  beforeUnmount() {
    destroy.destroyReactiveVueProps(this);
  },

  mounted() {
    this.parentEntryItemRef = this.$refs;
  },

  methods: {
    ...mapActions(useAssortmentCanvasStore, ['updateCanvasProduct']),

    onChange(changedData) {
      if (changedData.added) {
        const element = changedData.added.element;

        // - Update data
        // locallyUpdatedProduct is meant to be used before the changes are done on the server,
        // to avoid product dragging flickering
        const locallyUpdatedProduct = {
          ...element,
          currentSpacebreakId: this.spacebreakId,
        };
        this.updateCanvasProduct({
          _id: changedData.added.element._id,
          currentSpacebreakId: this.spacebreakId,
          locallyUpdatedProduct,
        });

        // - Flash product border to make clear what was moved
        setTimeout(() => {
          const productTileId = `tile-product-${element.productKey}`;
          // Setting the first child, `tile-product-{pkey}` is a wrapper over the main tile element.
          // This is important to draw the blue border around the product image after search correctly.
          const productElement = document.getElementById(productTileId).firstChild;

          this.addActiveSearchBorderToProductElement(productElement);
        }, 200);
      }
    },

    inReset(product) {
      return get(product, 'inReset', false);
    },

    isLocked(product) {
      return (
        product.lockType === assortmentProductLockTypes.lockTypes.locked || !this.inReset(product)
      );
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.title-container {
  white-space: nowrap;
  width: 100%;
  text-overflow: ellipsis;
  overflow: hidden;
  padding: 5px;
}

.title {
  overflow: hidden;
  padding: 0 9px 0 0;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.list-entry {
  width: 100%;
  padding: auto;
}

.list-entry-header {
  background-color: $canvas-list-entry-header-colour;
  border-radius: 4px;
  text-transform: uppercase;
  margin-bottom: 5px;
  height: 28px;

  &--active {
    background-color: $canvas-list-entry-header-active-bg-colour;
    color: $canvas-list-entry-header-active-colour;
  }
}

.neutral-border {
  border-color: black;
}

.entry-item {
  display: flex;
  height: 28px;
  align-items: center;
  position: relative;
  border-radius: 4px;
  border: 1px solid;
  margin-bottom: 5px;
  transition: all 1s ease-out;
}

.skeleton {
  img {
    width: 20px;
    height: 20px;
    margin-left: 5px;
  }
}
.locked {
  border-radius: 4px;
  flex: none;
  height: 18px;
  margin-right: 4px;
  padding: 0 10px;
  text-transform: capitalize;
}

:deep(.sortable-fallback) {
  display: none !important;
}

:deep(.v-chip.primary) {
  background-color: $assortment-text-colour !important;
}

:deep(.list-entry-header--active .v-chip.primary) {
  background-color: $canvas-list-entry-header-active-colour !important;
  color: $assortment-text-colour;
}

.pod-background-increase {
  background-color: $canvas-pod-increase-background;
}
.pod-background-decrease {
  background-color: $canvas-pod-decrease-background;
}
</style>
